import { useCallback, useEffect } from 'react'

import { useEditorContext } from '@editor/contexts/Editor'
import { ArcballControls } from '@editor/services/threejs'
import { useAppDispatch, useAppSelector } from '@editor/store/app'
import { useThree } from '@react-three/fiber'
import { PerspectiveCamera } from 'three'

import { setInitCameraCompleted, setInitControlsCompleted } from '../store/editor'

interface CameraManagerProps {
  windowSize: {
    width: number
    height: number
  }
}

const CameraManager = ({ windowSize }: CameraManagerProps) => {
  const { set, scene, camera, gl } = useThree()
  const { arcballControlsRef, initCompleted } = useEditorContext()

  const dispatch = useAppDispatch()
  const pcdBoundingSphereRadius = useAppSelector((state) => state.editor.pcdBoundingSphereRadius)
  const pcdBoundingBoxCenter = useAppSelector((state) => state.editor.pcdBoundingBoxCenter)

  const disablePanning = useAppSelector((state) => state.editor.disablePanning)
  const disableRotation = useAppSelector((state) => state.editor.disableRotation)
  const isDragging = useAppSelector((state) => state.toolUtil.core.isDragging)

  /**
   * Update controls based on state changes.
   */
  useEffect(() => {
    if (!arcballControlsRef.current) return

    arcballControlsRef.current.enabled = !isDragging
    arcballControlsRef.current.enablePan = !disablePanning
    arcballControlsRef.current.enableRotate = !disableRotation
  }, [disablePanning, disableRotation, isDragging, arcballControlsRef])

  /**
   * Initialize the scene camera.
   * This runs only once when the component mounts.
   */
  useEffect(() => {
    if (!pcdBoundingSphereRadius || !pcdBoundingBoxCenter || !initCompleted) return

    // Initialize camera
    if (camera instanceof PerspectiveCamera) {
      camera.aspect = windowSize.width / windowSize.height
      camera.fov = 90
      camera.near = 0.1
      camera.far = 2000
      camera.position.set(
        pcdBoundingBoxCenter[0],
        pcdBoundingBoxCenter[1] - pcdBoundingSphereRadius,
        pcdBoundingBoxCenter[2],
      )
      camera.up.set(0, 0, 1)
      camera.updateProjectionMatrix()
    }

    dispatch(setInitCameraCompleted(true))
  }, [pcdBoundingBoxCenter, pcdBoundingSphereRadius, windowSize, camera, initCompleted, dispatch])

  /**
   * Initialize ArcballControls for the scene.
   */
  const initControls = useCallback(() => {
    if (arcballControlsRef.current) arcballControlsRef.current.dispose()
    arcballControlsRef.current = new ArcballControls(camera, gl.domElement, scene)
    arcballControlsRef.current.enableAnimations = false
    arcballControlsRef.current.enableFocus = false // We want finer control over focus. It'll be handled by the CameraFocus tool.
    arcballControlsRef.current.activateGizmos(false)
    arcballControlsRef.current.setGizmosVisible(false)

    set({
      controls: arcballControlsRef.current,
    })
  }, [arcballControlsRef, camera, gl, scene, set])

  /**
   * Initialize the scene with controls.
   * This runs only once when the component mounts.
   */
  useEffect(() => {
    // Set controls
    initControls()
    dispatch(setInitControlsCompleted(true))

    return () => {
      if (arcballControlsRef.current) {
        arcballControlsRef.current.dispose()
        arcballControlsRef.current = null
      }
    }
  }, [arcballControlsRef, initCompleted, initControls, dispatch])

  /**
   * Re-initialize controls on window resize.
   * This ensures the controls adapt to the new window dimensions.
   */
  useEffect(() => {
    const handleResize = () => {
      initControls()
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [camera, gl, initControls])

  return null
}

export default CameraManager
